import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import ReactFlow, { Background, Controls, MiniMap } from "reactflow";
import "reactflow/dist/style.css";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { GitBranch, Timer, ChartBar } from "@phosphor-icons/react";

export default function ProcessOrchestration() {
  const [processes, setProcesses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showVariants, setShowVariants] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/processes");
      setProcesses(data);
      setSelectedId(data[0]?.id);
    })();
  }, []);

  const selected = useMemo(
    () => processes.find((p) => p.id === selectedId),
    [processes, selectedId]
  );

  const hasVariantData = !!(selected?.top_variants && selected.top_variants.length > 0);

  return (
    <div className="space-y-6" data-testid="process-page">
      <div>
        <div className="data-label">Discovery</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Process Orchestration</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">Mined process models with variants, cycle-time metrics, and bottleneck signals from the Process Mining agent.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Process list */}
        <div className="itsme-card p-3 lg:col-span-1 space-y-2 max-h-[600px] overflow-auto">
          <div className="data-label px-2 py-2">Discovered Processes</div>
          {processes.map((p) => (
            <button
              key={p.id}
              data-testid={`proc-select-${p.id}`}
              onClick={() => { setSelectedId(p.id); setShowVariants(false); }}
              className={`w-full text-left p-3 rounded-md transition-colors border ${
                selectedId === p.id
                  ? "bg-[#1C1F26] border-[#0055FF]"
                  : "bg-transparent border-transparent hover:bg-[#13151A]"
              }`}
            >
              <div className="text-sm font-medium flex items-center gap-2">
                {p.id.startsWith("proc-mined-") && (
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">
                    Mined
                  </span>
                )}
                {p.name}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{p.domain}</div>
              <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                <span>{p.cases} cases</span>
                <span>·</span>
                <span>{p.variants} variants</span>
              </div>
              <div className="mt-2">
                <Progress value={p.health} className="h-1 bg-[#272A35]" />
                <div className="text-[10px] text-slate-500 mt-1">Health {p.health}/100</div>
              </div>
            </button>
          ))}
        </div>

        {/* Flow */}
        <div className="itsme-card lg:col-span-3 p-4 flex flex-col">
          {selected && (
            <>
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0">
                  <div className="data-label">Process Model</div>
                  <div className="font-heading text-xl font-semibold mt-1">{selected.name}</div>
                  <div className="text-xs mt-1 flex items-center gap-1.5 flex-wrap">
                    <Timer size={12} className="text-[#FFB000]" />
                    <span className="text-slate-400">Bottleneck:</span>
                    <span className="text-[#FF3B30]">{selected.bottleneck}</span>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 text-right shrink-0">
                  <Metric label="Cycle" value={`${selected.metrics.avg_cycle_time_hours}h`} />
                  <Metric label="Throughput" value={`${selected.metrics.throughput_per_day}/d`} />
                  <Metric label="Error" value={`${selected.metrics.error_rate_pct}%`} />
                  <Metric label="SLA" value={`${selected.metrics.sla_compliance_pct}%`} />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Button size="sm" variant={!showVariants ? "default" : "outline"}
                        onClick={() => setShowVariants(false)}
                        data-testid="view-flow"
                        className={!showVariants ? "bg-[#0055FF]" : "border-[#272A35] text-slate-300"}>
                  <GitBranch size={13} className="mr-1" /> Process Flow
                </Button>
                {hasVariantData && (
                  <Button size="sm" variant={showVariants ? "default" : "outline"}
                          onClick={() => setShowVariants(true)}
                          data-testid="view-variants"
                          className={showVariants ? "bg-[#0055FF]" : "border-[#272A35] text-slate-300"}>
                    <ChartBar size={13} className="mr-1" /> Variant Explorer ({selected.top_variants.length})
                  </Button>
                )}
              </div>

              {!showVariants ? (
                <div className="h-[420px] rounded-md border border-[#272A35] overflow-hidden">
                  <ReactFlow
                    nodes={selected.graph.nodes}
                    edges={selected.graph.edges}
                    fitView
                    nodesDraggable={false}
                    nodesConnectable={false}
                  >
                    <Background color="#272A35" gap={20} />
                    <Controls className="!bg-[#13151A]" />
                    <MiniMap nodeColor="#00E5FF" maskColor="rgba(9,10,14,0.7)" style={{ background: "#0B0D12" }} />
                  </ReactFlow>
                </div>
              ) : (
                <VariantExplorer variants={selected.top_variants} totalCases={selected.cases} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div>
    <div className="data-label text-right">{label}</div>
    <div className="font-heading text-base font-semibold">{value}</div>
  </div>
);

const VariantExplorer = ({ variants, totalCases }) => {
  const maxCases = Math.max(...variants.map((v) => v.case_count), 1);
  const maxCycle = Math.max(...variants.map((v) => v.p95_cycle_hours), 1);

  return (
    <div className="space-y-3 max-h-[500px] overflow-auto pr-1" data-testid="variant-explorer">
      {variants.map((v, i) => {
        const casesBar = (v.case_count / maxCases) * 100;
        const cycleBar = (v.p95_cycle_hours / maxCycle) * 100;
        return (
          <div key={i} data-testid={`variant-${i}`}
               className="itsme-card p-4 itsme-card-hover">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-mono text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded px-2 py-0.5">
                    Variant {i + 1}
                  </span>
                  <span className="text-slate-500 text-xs">{v.case_count} of {totalCases} cases</span>
                  <span className="text-slate-500 text-xs">·</span>
                  <span className="text-slate-300 text-xs font-mono">{v.share_pct}%</span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {v.activities.map((a, ai) => (
                    <div key={ai} className="flex items-center gap-2">
                      <span className="bg-[#13151A] border border-[#272A35] rounded px-2 py-1 text-xs text-slate-200">{a}</span>
                      {ai < v.activities.length - 1 && <span className="text-slate-600">›</span>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-right shrink-0">
                <div>
                  <div className="data-label text-right">Median</div>
                  <div className="font-heading text-lg font-bold text-slate-100">{fmtHours(v.median_cycle_hours)}</div>
                </div>
                <div>
                  <div className="data-label text-right">Avg</div>
                  <div className="font-heading text-lg font-bold text-slate-100">{fmtHours(v.avg_cycle_hours)}</div>
                </div>
                <div>
                  <div className="data-label text-right">p95</div>
                  <div className="font-heading text-lg font-bold text-[#FFB000]">{fmtHours(v.p95_cycle_hours)}</div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="data-label">Case share</span>
                  <span className="text-xs font-mono text-[#00E5FF]">{v.case_count}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#272A35] overflow-hidden">
                  <div className="h-full bg-[#00E5FF]" style={{ width: `${casesBar}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="data-label">p95 cycle</span>
                  <span className="text-xs font-mono text-[#FFB000]">{fmtHours(v.p95_cycle_hours)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#272A35] overflow-hidden">
                  <div className="h-full bg-[#FFB000]" style={{ width: `${cycleBar}%` }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function fmtHours(h) {
  if (h == null || h === 0) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}
